import type { ReactElement } from 'react';
import { APP_DISPLAY_NAME, personalisierungLogoPfad } from '@mcikm-vote/shared';

import logoDunkel from '../assets/mcikmVOTE-darkmode.svg';
import logoHell from '../assets/mcikmVOTE-lightmode.svg';
import { usePersonalisierung } from '../personalisierung/personalisierungApi';
import { useTheme } from '../theme/ThemeProvider';

/**
 * Der Anwendungsname als Grafik — und seit ÄA-5 wahlweise das Logo des Betreibers.
 *
 * **Die Auswahl folgt dem tatsächlich eingestellten Erscheinungsbild**, nicht der
 * Betriebssystemeinstellung: §21 erlaubt jederzeit eine manuelle Umschaltung, und ein
 * `prefers-color-scheme`-Umschalter zeigte danach das falsche Logo. `useTheme()` liefert genau den
 * Wert, der auch als `data-theme` am Wurzelelement steht. Das gilt für beide Fassungen — das
 * mitgelieferte Markenlogo wie das hochgeladene Firmenlogo.
 *
 * **Der Anwendungsname bleibt als Alternativtext erhalten.** Er ist nach §9.1 das Einzige, was vor
 * der Anmeldung sichtbar sein darf — er darf nicht davon abhängen, ob eine Grafik geladen werden
 * kann, und muss für Vorlesewerkzeuge lesbar bleiben (§19.1). Ein **eigenes** Logo trägt deshalb
 * keinen Alternativtext: Es ist dann Schmuck, und {@link Markenkopf} ergänzt den Anwendungsnamen
 * als sichtbaren Text — sonst verschwände er mit der Marke.
 *
 * Die mitgelieferten Dateien liegen im Anwendungspaket; es wird nichts von fremden Servern
 * nachgeladen (§26). Ein eigenes Logo kommt aus der eigenen Datenbank, ebenfalls nicht von außen.
 */

/** Seitenverhältnis der Vorlage: 960 × 240. Als Attribute gesetzt, damit nichts nachspringt. */
const BREITE = 960;
const HOEHE = 240;

/**
 * Höhen der beiden Ausprägungen.
 *
 * Deutlich größer, als die Ersetzung eines Schriftzugs vermuten ließe: Die Wortmarke füllt nur
 * etwa ein Drittel der Grafikhöhe, der Rest ist Bildmarke und Freiraum. Bei der Höhe der früheren
 * Schrift wären die Buchstaben nur noch halb so groß gewesen wie zuvor.
 */
const HOEHENKLASSE = { klein: 'h-10', gross: 'h-16' } as const;

function klassen(gross: boolean, className: string): string {
  return ['w-auto', gross ? HOEHENKLASSE.gross : HOEHENKLASSE.klein, className]
    .filter(Boolean)
    .join(' ');
}

/**
 * Ausschließlich das mitgelieferte mcikmVOTE-Markenlogo, ohne Personalisierung.
 *
 * Die Fußzeile nutzt es: Dort geht es um die Anwendung selbst, nicht um den Betreiber (ÄA-5).
 */
export function Markenlogo({
  gross = false,
  className = '',
}: {
  gross?: boolean;
  className?: string;
}): ReactElement {
  const { resolved } = useTheme();

  return (
    <img
      src={resolved === 'dark' ? logoDunkel : logoHell}
      alt={APP_DISPLAY_NAME}
      width={BREITE}
      height={HOEHE}
      className={klassen(gross, className)}
    />
  );
}

/**
 * Adresse des eigenen Logos für das eingestellte Erscheinungsbild — oder `undefined`.
 *
 * Auch von {@link Markenkopf} genutzt, um zu entscheiden, ob der Anwendungsname zusätzlich als
 * Text erscheinen muss.
 */
export function useEigenesLogo(): string | undefined {
  const { resolved } = useTheme();
  const personalisierung = usePersonalisierung();
  const daten = personalisierung.data;

  if (daten === undefined) {
    return undefined;
  }

  const variante = resolved === 'dark' ? 'dunkel' : 'hell';
  const angabe = variante === 'dunkel' ? daten.logoDunkel : daten.logoHell;

  return angabe === null ? undefined : personalisierungLogoPfad(variante, daten.stand);
}

/**
 * Das Logo im Kopf und über den Anmeldungen — das eigene, sonst das Markenlogo.
 *
 * Die Aufrufstellen bleiben unverändert; ob personalisiert wird, entscheidet sich hier.
 */
export function Logo({
  gross = false,
  className = '',
}: {
  /** Größere Darstellung für Anmeldeseiten; sonst die Kopfzeilenhöhe. */
  gross?: boolean;
  className?: string;
}): ReactElement {
  const eigenes = useEigenesLogo();

  if (eigenes === undefined) {
    return <Markenlogo gross={gross} className={className} />;
  }

  // Ohne feste Maße: Ein hochgeladenes Logo hat ein unbekanntes Seitenverhältnis. Die Höhe steht
  // fest, die Breite ergibt sich — ein erzwungenes 4:1 verzerrte jedes quadratische Logo.
  return <img src={eigenes} alt="" className={klassen(gross, className)} />;
}
